import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle2, XCircle } from 'lucide-react';
import { industryFocusData } from '../mockData';

const IndustryFocus = () => {
  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-200 px-4 py-1.5 text-sm font-semibold">
            INDUSTRY EXPERTISE
          </Badge>
          <h2 className="text-5xl font-bold text-slate-900 mb-6">
            We Know Your Business Inside Out
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Our AI solutions are purpose-built for your industry's unique challenges and opportunities
          </p>
        </div>

        {/* Industry Cards */}
        <div className="space-y-12">
          {industryFocusData.map((industry) => (
            <Card 
              key={industry.id}
              className="overflow-hidden border-2 hover:border-blue-500 transition-all duration-300 hover:shadow-xl"
            >
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Image Side */}
                <div className="relative h-64 lg:h-auto overflow-hidden">
                  <img 
                    src={industry.image}
                    alt={industry.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-transparent flex items-center">
                    <div className="p-8">
                      <h3 className="text-3xl font-bold text-white mb-3">
                        {industry.name}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {industry.industries.map((ind, idx) => (
                          <Badge 
                            key={idx}
                            variant="outline"
                            className="bg-white/10 border-white/30 text-white backdrop-blur-sm"
                          >
                            {ind}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content Side */}
                <CardContent className="p-8 lg:p-10 flex flex-col justify-center bg-slate-50">
                  {/* Challenges */}
                  <div className="mb-8">
                    <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-red-500" />
                      Common Challenges:
                    </h4>
                    <ul className="space-y-3">
                      {industry.challenges.map((challenge, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-slate-700">
                          <span className="text-red-500 font-bold text-lg leading-none">✗</span>
                          <span>{challenge}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Solutions */}
                  <div className="mb-6">
                    <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      Our AI Solutions:
                    </h4>
                    <ul className="space-y-3">
                      {industry.solutions.map((solution, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-slate-700">
                          <span className="text-green-500 font-bold text-lg leading-none">✓</span>
                          <span className="font-medium">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* ROI Badge */}
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-4 rounded-lg inline-block">
                      <div className="text-sm font-semibold mb-1">PROVEN RESULTS</div>
                      <div className="text-xl font-bold">{industry.roi}</div>
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustryFocus;
